import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Lock, LogOut, Loader2, ArrowLeft } from 'lucide-react';
import { AdminItemManager } from '../components/AdminItemManager';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'athena_admin_token';

const PICK_FIELDS = [
  { key: 'name_en', label: 'Name (English)', required: true },
  { key: 'name_es', label: 'Name (Spanish)', required: true },
  { key: 'tag_en', label: 'Tag (English)', placeholder: 'e.g., Footwear', required: true },
  { key: 'tag_es', label: 'Tag (Spanish)', placeholder: 'e.g., Calzado', required: true },
  { key: 'price', label: 'Price', placeholder: 'e.g., $45 BZD', required: true },
  { key: 'source_url', label: 'Source URL (private)', placeholder: 'https://www.amazon.com/...', hint: 'Where you buy it. Never shown to customers.' }
];

const PRODUCT_FIELDS = [
  { key: 'title_en', label: 'Title (English)', required: true },
  { key: 'title_es', label: 'Title (Spanish)', required: true },
  { key: 'category_en', label: 'Category (English)', placeholder: 'e.g., Sneakers' },
  { key: 'category_es', label: 'Category (Spanish)', placeholder: 'e.g., Zapatillas' },
  { key: 'store', label: 'Store', placeholder: 'e.g., Nike', required: true },
  { key: 'price', label: 'Price', placeholder: 'e.g., $60 BZD' },
  { key: 'store_url', label: 'Store URL (private)', placeholder: 'https://www.nike.com/...', hint: 'Where you buy it. Never shown to customers.' }
];

const AdminPage = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setPassword('');
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Login failed');
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success('Welcome back!');
    } catch (err) {
      toast.error(err.message || 'Login failed');
    } finally {
      setLoggingIn(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#8b1538] via-[#a01e47] to-[#1e1919] px-4">
        <Card className="w-full max-w-md border-2 border-[#fad24b]/30 shadow-2xl" data-testid="admin-login-card">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-[#8b1538]/10 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-[#8b1538]" />
              </div>
              <h1 className="text-2xl font-bold text-[#1e1919]">Admin Login</h1>
              <p className="text-[#736c64] mt-1">Manage your homepage picks & products</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <Label htmlFor="admin-email" className="text-[#1e1919] font-semibold">Email</Label>
                <Input id="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" className="mt-2 border-[#736c64]/30 focus:border-[#8b1538]" placeholder="admin@athenasoss.bz" required />
              </div>
              <div>
                <Label htmlFor="admin-password" className="text-[#1e1919] font-semibold">Password</Label>
                <Input id="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" className="mt-2 border-[#736c64]/30 focus:border-[#8b1538]" required />
              </div>
              <Button type="submit" disabled={loggingIn} data-testid="admin-login-btn" className="w-full bg-[#8b1538] hover:bg-[#6b0f2a] text-white py-6 text-lg font-semibold">
                {loggingIn ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Log In'}
              </Button>
            </form>
            <button onClick={() => navigate('/')} className="mt-6 w-full text-center text-sm text-[#736c64] hover:text-[#8b1538]">← Back to website</button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f5f2]" data-testid="admin-dashboard">
      <header className="bg-[#1e1919] text-white">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/')} className="text-white hover:text-[#fad24b]">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-bold">Athena&apos;s Admin</h1>
          </div>
          <Button onClick={logout} data-testid="admin-logout-btn" variant="ghost" className="text-white hover:text-[#fad24b]">
            <LogOut className="w-4 h-4 mr-2" /> Log Out
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <Tabs defaultValue="picks" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="picks" data-testid="tab-picks">Top Picks</TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products">Trending Products</TabsTrigger>
          </TabsList>
          <TabsContent value="picks">
            <AdminItemManager token={token} onUnauthorized={logout} resource="picks" fields={PICK_FIELDS} />
          </TabsContent>
          <TabsContent value="products">
            <AdminItemManager token={token} onUnauthorized={logout} resource="products" fields={PRODUCT_FIELDS} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;
